import { IEvent, IHost } from "@Interfaces";
import { getEventCurrency } from "@Modules/eventPage/utils/getEventCurrency";
import { getAreIntlPaymentsEnabled } from "@Modules/eventPage/utils/getAreIntlPaymentsEnabled";
import { getUpsellListingsForEventId } from "@Modules/eventPage/components/UpsellListings/utils/getUpsellListingsForEventId";
import {
    isMerchandise,
    useSetQuantity,
} from "@Modules/eventPage/utils/merchandise";
import {
    ACTION_TYPES,
    API_ACTION_TYPES,
    createAction,
    FETCH_STATUS,
    isBrowser,
    isEmpty,
    SELECTORS,
    useEventDataOfSelectedEvent,
    useFetchStatus,
} from "@Utils";
import { EVENT_ENUM } from "@Utils/constants/event";
import { promotion_types_constants } from "@Utils/constants/promotionTypeConstants";
import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useSetNumberOfCustomers } from "@Modules/eventPage/utils/useSetNumberOfCustomers";
import { LISTING_TYPES } from "repoV2/constants/listing";
import { getWorkshopSlots } from "@Modules/eventPage/utils/getWorkshopSlots";
import { checkIsArrayEmpty } from "repoV2/utils/common/dataTypes/array";
import { constructRoute } from "repoV2/utils/urls&routing/routing";
import { APP_ROUTE_KEYS } from "repoV2/constants/urls&routing/routing";
import { postAnalytics } from "repoV2/features/Common/Analytics/Analytics.utils";
import { ANALYTICS_EVENTS } from "repoV2/features/Common/Analytics/Analytics.constants";
import { getShowUpsellListings } from "./getShowUpsellListings";

export const useUpsellListings = ({
    shouldRedirectToOffering, // if false then not to redirect to offering page even if enable_redirection_on_order_bump true
}: {
    shouldRedirectToOffering?: boolean;
}) => {
    const {
        disablePayment,
        urlQueryParams,
        resetDiscount,
        setAppliedDiscountCodeDetails,
        setShowLoyaltyDiscountSection,
    } = useSelector(SELECTORS.upsellProps);
    const dispatch = useDispatch();
    const host = useSelector(SELECTORS.host);

    const { hostName } = host || {};

    const hostData = host?.dataHost?.profile_data || ({} as IHost.IHostData);
    const {
        event,
        eventData = {} as IEvent.IEventData, // in cart page, eventData is undefined on initial load
    } = useEventDataOfSelectedEvent();
    // Initial Quantity -> from Admin panel
    const {
        batched_flexible_sessions_count: batchedFlexibleSessionsCount,
        custom_order_bump_heading: customOrderBumpHeading, // section name of order bumps
        enable_redirection_on_order_bump: enableRedirectionOnOrderBump, // if true redirect to offering page onClick of card
    } = eventData?.metadata || ({} as IEvent.IEventData["metadata"]);

    const eventId = eventData.uuid;
    const setQuantity = useSetQuantity();
    const setNumberOfCustomers = useSetNumberOfCustomers();

    // Whether upsell is to be shown
    const showUpsellListings = getShowUpsellListings({
        hostData,
        eventData,
        disablePayment,
        urlQueryParams,
    });

    const {
        upsellListings: upsellListingsCollection, // Net available listings for upsell
    }: IEvent.IStore = event;

    const { eventCurrency } = getEventCurrency({
        eventData,
    });

    const upsellFetchStatus = useFetchStatus(API_ACTION_TYPES.FETCH_UPSELL);

    useEffect(() => {
        // TODO: figure out why did we check for upsell listings of the first element
        // and not for the current eventId
        const upsellListingsData = Object.values(
            upsellListingsCollection
        )?.[0]?.[0];

        // Whether a re-fetch is needed for updated currency
        const isCurrencyRefetch: boolean =
            upsellListingsData &&
            upsellListingsData?.currency_name !== eventCurrency;

        // Whether a fetch needs to be made for upsell
        const isUpsellFetchNeeded: boolean =
            upsellFetchStatus === FETCH_STATUS.NOT_FETCHED ||
            (upsellFetchStatus === FETCH_STATUS.FETCHED &&
                !(eventId in upsellListingsCollection)) ||
            isCurrencyRefetch;

        // Incase we do not need to show the upsell listings,
        // say we have a plan with two listings, one is habing an order bump, and other
        // is not having it, then say user selects the listing with order bump, then we
        // save its data in redux, now if user switches the listing, the we won't be fetching the order bumps again
        // as showUpsellListings will not be true, so we need to make it certain that we have cleared the upsell listings
        // data from the redux as well
        if (!showUpsellListings) {
            setSelectedUpsellOfferingQuantities({});
            clearUpsellListingsData();
        }

        // isBrowser() - only call the api client side
        if (isBrowser() && showUpsellListings && isUpsellFetchNeeded) {
            dispatch(
                createAction(ACTION_TYPES.UTILS.API_CALL, {
                    apiActionType: API_ACTION_TYPES.FETCH_UPSELL,
                    urlArgs: {
                        listingID: eventId,
                        currency: eventCurrency,
                        upsellType: promotion_types_constants.ORDER_BUMPS, // Order bump will be hardcoded for now untill we integrate other promotion types
                    },
                    successCallback: response => {
                        /// when fetching or refetching of order bumps,
                        // reset the selected offerings as well
                        // to remove the stale value of selected order bumps in redux
                        // e.g. in case of plans with 2 listings, each listing having a different order bump
                        // then on change of listings in plan dropdown, we only want to keep the latest one
                        // otherwise it shows higher price on payment gateway
                        let updatedSelectedUpsellOfferingQuantities = {};
                        response?.response?.data?.upsell_listings.forEach(
                            (item: IEvent.IUpsellOffering) => {
                                const isWorkshop =
                                    item.type === LISTING_TYPES.WORKSHOPS;
                                let selectedSlotUuids: Array<string> = [];
                                if (isWorkshop) {
                                    selectedSlotUuids = getWorkshopSlots(
                                        item?.slots
                                    );
                                    if (checkIsArrayEmpty(selectedSlotUuids)) {
                                        alert("no slots available!");
                                        return;
                                    }
                                }
                                if (item.allow_order_bump_in_purchase) {
                                    updatedSelectedUpsellOfferingQuantities = {
                                        ...updatedSelectedUpsellOfferingQuantities,
                                        [item.additional_listing_uid]: {
                                            ...(isMerchandise(item) && {
                                                quantity: 1,
                                            }),
                                            ...(isWorkshop && {
                                                slot_uids: selectedSlotUuids,
                                            }),
                                        },
                                    };
                                }
                            }
                        );
                        setSelectedUpsellOfferingQuantities(
                            updatedSelectedUpsellOfferingQuantities
                        );
                    },
                })
            );
        }
    }, [upsellFetchStatus, eventCurrency, eventId]);

    const areIntlPaymentsEnabled: boolean = getAreIntlPaymentsEnabled({
        host,
        eventData,
    });

    const upsellListings: Array<IEvent.IUpsellOffering> =
        getUpsellListingsForEventId({
            eventId,
            event,
        });

    const {
        otherListings: selectedUpsellOfferingQuantities,
        numberOfCustomers,
    } = event.paymentInfo;

    const updateBookingData = (data: Partial<IEvent.IPaymentInfo>) => {
        dispatch(createAction(ACTION_TYPES.EVENT.UPDATE_BOOKING_DATA, data));
    };

    const clearUpsellListingsData = () => {
        dispatch(createAction(ACTION_TYPES.EVENT.RESET_UPSELL_LISTINGS));
    };

    const isInSelectedUpsellOfferingQuantities = (
        item: IEvent.IUpsellOffering
    ) => item.additional_listing_uid in selectedUpsellOfferingQuantities;

    const setSelectedUpsellOfferingQuantities = (
        otherListingsProp: IEvent.IPaymentInfo["otherListings"]
    ) => updateBookingData({ otherListings: otherListingsProp });

    const handleCardClick = (otherListing: IEvent.IUpsellOffering) => {
        const redirectToOffering =
            shouldRedirectToOffering !== undefined
                ? shouldRedirectToOffering
                : enableRedirectionOnOrderBump || false;

        if (redirectToOffering) {
            handleRedirectToOfferingPage({
                additionalListingUuid: otherListing.additional_listing_uid,
            });
        } else {
            toggleOfferingInSelectedUpsellOfferingQuantities(otherListing);
        }
    };

    const handleRedirectToOfferingPage = ({
        additionalListingUuid,
    }: {
        additionalListingUuid: string;
    }) => {
        const additionalOfferingPageUrl = constructRoute({
            appRouteKey: APP_ROUTE_KEYS.EVENT,
            args: {
                slug: additionalListingUuid,
            },
        });
        window.open(`${window.location.origin}${additionalOfferingPageUrl}`);
        // router.push(additionalOfferingPageUrl);
    };

    const toggleOfferingInSelectedUpsellOfferingQuantities = (
        otherListing: IEvent.IUpsellOffering
    ): void => {
        const isWorkshop = otherListing.type === LISTING_TYPES.WORKSHOPS;
        let selectedSlotUuids: Array<string> = [];
        if (isWorkshop) {
            selectedSlotUuids = getWorkshopSlots(otherListing?.slots);
            if (checkIsArrayEmpty(selectedSlotUuids)) {
                alert("no slots available!");
                return;
            }
        }

        /* 
           Below if Statement is moved from Old Event Page on 26 may 2023 by @ishtiaq.
           Reseting Quantity, Discounts, Coupouns -> 
           when Upsells are added. Because Discounts we added earlier and then if upsell is added the calculation is incorrect. 
           */
        resetDiscount();
        setQuantity(batchedFlexibleSessionsCount || 1);
        setNumberOfCustomers(1);
        setAppliedDiscountCodeDetails("not applied");
        setShowLoyaltyDiscountSection(true);

        if (isInSelectedUpsellOfferingQuantities(otherListing)) {
            delete selectedUpsellOfferingQuantities[
                otherListing.additional_listing_uid
            ];
            setSelectedUpsellOfferingQuantities(
                selectedUpsellOfferingQuantities
            );
        } else {
            const updatedSelectedUpsellOfferingQuantities = {
                ...selectedUpsellOfferingQuantities,
                [otherListing.additional_listing_uid]: {
                    ...(isMerchandise(otherListing) && { quantity: 1 }),
                    ...(isWorkshop && {
                        slot_uids: selectedSlotUuids,
                    }),
                },
            };

            setSelectedUpsellOfferingQuantities(
                updatedSelectedUpsellOfferingQuantities
            );

            const { title, type, currency_name: currencyName } = otherListing;

            const upsellListingUuidMap = upsellListings.reduce(
                (map, lisiting) => ({
                    ...map,
                    [lisiting.additional_listing_uid]: lisiting,
                }),
                {} as { [uuid: string]: IEvent.IUpsellOffering }
            );
            const selectedUpsellListingsUuids = Object.keys(
                updatedSelectedUpsellOfferingQuantities
            );
            const totalUpsellPrice = selectedUpsellListingsUuids.reduce(
                (totalPrice, upsellListingUuid) =>
                    totalPrice +
                    upsellListingUuidMap[upsellListingUuid].price_per_head,
                0
            );

            postAnalytics(ANALYTICS_EVENTS.ADD_TO_CART, {
                sub_domain: hostName,
                event_source_url: window.location.href,
                content_name: title,
                content_category: EVENT_ENUM[type].TITLE,
                currency: currencyName,
                value: totalUpsellPrice,
                items: selectedUpsellListingsUuids.map(uuid => ({
                    item_id: uuid,
                    item_name: upsellListingUuidMap[uuid]?.title,
                })),
            });
        }
    };

    const quantity = (item: IEvent.IUpsellOffering): number =>
        selectedUpsellOfferingQuantities?.[item?.additional_listing_uid]
            ?.quantity ?? 1;

    const hasMinQuantity = (item: IEvent.IUpsellOffering): boolean =>
        quantity(item) <= 1;

    const hasMaxQuantity = (item: IEvent.IUpsellOffering): boolean => {
        return (
            numberOfCustomers * (quantity(item) + 1) >
            item.total_inventory - item.purchased_inventory
        );
    };

    const handleQtyChange = (
        item: IEvent.IUpsellOffering,
        isIncrement: boolean = false
    ) => {
        if (
            !(isMerchandise(item) && isInSelectedUpsellOfferingQuantities(item))
        )
            return;

        if (isIncrement && hasMaxQuantity(item)) {
            alert("Maximum available inventory reached");
            return;
        }

        if (!isIncrement && hasMinQuantity(item)) {
            toggleOfferingInSelectedUpsellOfferingQuantities(item);
            return;
        }

        setSelectedUpsellOfferingQuantities({
            ...selectedUpsellOfferingQuantities,
            [item.additional_listing_uid]: {
                ...selectedUpsellOfferingQuantities?.[
                    item?.additional_listing_uid
                ],
                quantity: quantity(item) + (isIncrement ? 1 : -1),
            },
        });
    };

    const hideItemPrice = (item: IEvent.IUpsellOffering) =>
        hostData?.hide_price_for_zero_priced_order_bump_listing &&
        !item.updated_price;

    return {
        hideComponent: !(
            showUpsellListings &&
            (!isEmpty(upsellListings) ||
                upsellFetchStatus === FETCH_STATUS.FETCHING)
        ),
        sectionTitle: customOrderBumpHeading || "Bought together",
        showListingOriginalPrice: (item: IEvent.IUpsellOffering) =>
            item.show_strikethrough,
        isLoading: upsellFetchStatus !== FETCH_STATUS.FETCHED,
        upsellListings,
        getCheckboxText: (item: IEvent.IUpsellOffering) =>
            item.purchase_button_cta ?? "Yes! Add this to my purchase",
        eventCurrencySymbol: getEventCurrency({ eventData })
            .eventCurrencySymbol,
        toggleOfferingInSelectedUpsellOfferingQuantities,
        handleQtyChange,
        areIntlPaymentsEnabled,
        quantity,
        hasMaxQuantity,
        isInSelectedUpsellOfferingQuantities,
        handleCardClick,
        hideItemPrice,
    };
};
